import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Link, useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_RECIPES,
  GET_PRODUCTS,
  GET_INVENTORY_INGREDIENTS,
  CREATE_RECIPE,
  UPDATE_RECIPE,
  DELETE_RECIPE,
} from "./queries";
import {
  UNIT_OPTIONS,
  DEFAULT_CONVERSIONS,
  Recipe,
  RecipeIngredientInput,
  InventoryIngredient,
} from "./models";
import { DeleteModal } from "../../modals/deletes";

type Params = {
  id?: string;
};

interface IngredientRow {
  key: number;
  ingredient_id: string;
  ingredientName: string;
  quantity: string;
  unit: string;
  availableUnits: string[];
  is_optional: boolean;
  waste_factor: string;
  notes: string;
  cost: number;
}

const getAvailableUnits = (ing: InventoryIngredient): string[] => {
  const units = new Set<string>();
  units.add(ing.unit);
  // Add units from explicit conversions
  if (ing.conversions) {
    for (const conv of ing.conversions) {
      units.add(conv.from_unit);
      units.add(conv.to_unit);
    }
  }
  // Add units from default conversions (kg↔g, l↔ml, lb↔oz)
  for (const key of Object.keys(DEFAULT_CONVERSIONS)) {
    const [from, to] = key.split(":");
    if (from === ing.unit) units.add(to);
    if (to === ing.unit) units.add(from);
  }
  return Array.from(units);
};

// Returns how many base units 1 of `selectedUnit` equals.
// e.g. if base is "kg" and conversion is "1 kg = 1000 g", then
// getConversionToBase("g", ing) = 0.001 (1g = 0.001kg)
const getConversionToBase = (
  selectedUnit: string,
  ing: InventoryIngredient
): number => {
  if (selectedUnit === ing.unit) return 1;
  // Check explicit ingredient conversions first
  if (ing.conversions) {
    for (const conv of ing.conversions) {
      if (conv.from_unit === selectedUnit && conv.to_unit === ing.unit) {
        return conv.factor;
      }
      if (conv.to_unit === selectedUnit && conv.from_unit === ing.unit) {
        return 1 / conv.factor;
      }
    }
  }
  // Fall back to default conversions
  const defaultFactor = DEFAULT_CONVERSIONS[`${selectedUnit}:${ing.unit}`];
  if (defaultFactor) return defaultFactor;
  return 1;
};

const emptyRow = (key: number): IngredientRow => ({
  key,
  ingredient_id: "",
  ingredientName: "",
  quantity: "",
  unit: "g",
  availableUnits: [],
  is_optional: false,
  waste_factor: "1",
  notes: "",
  cost: 0,
});

const RecipeForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const isEdit = !!id;

  // Form state
  const [productId, setProductId] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [name, setName] = useState("");
  const [yieldQty, setYieldQty] = useState("1");
  const [notes, setNotes] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Ingredient rows
  const [rowKey, setRowKey] = useState(1);
  const [rows, setRows] = useState<IngredientRow[]>([emptyRow(0)]);

  // Product search
  const [productSearch, setProductSearch] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // Ingredient search per row
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);

  // Queries
  const [fetchRecipes, { data: recipesData, loading }] = useLazyQuery(
    GET_RECIPES,
    { fetchPolicy: "no-cache" }
  );
  const [fetchProducts, { data: productsData }] = useLazyQuery(GET_PRODUCTS, {
    fetchPolicy: "no-cache",
  });
  const [fetchIngredients, { data: ingredientsData }] = useLazyQuery(
    GET_INVENTORY_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );

  // Mutations
  const [createRecipe] = useMutation(CREATE_RECIPE);
  const [updateRecipe] = useMutation(UPDATE_RECIPE);
  const [deleteRecipe] = useMutation(DELETE_RECIPE);

  useEffect(() => {
    fetchProducts({ variables: { take: 500 } });
    fetchIngredients({ variables: { take: 500 } });
    if (isEdit) {
      fetchRecipes({ variables: { take: 500 } });
    }
    //eslint-disable-next-line
  }, []);

  // Load existing recipe data for editing
  useEffect(() => {
    if (isEdit && recipesData?.getRecipes && ingredientsData?.getInventoryIngredients) {
      const allIngredients: InventoryIngredient[] = ingredientsData.getInventoryIngredients;
      const recipe: Recipe | undefined = recipesData.getRecipes.find(
        (r: Recipe) => r.id === id
      );
      if (recipe) {
        setProductId(recipe.product_id.toString());
        setSizeId(recipe.size_id?.toString() || "");
        setName(recipe.name || "");
        setYieldQty(recipe.yield_qty?.toString() || "1");
        setNotes(recipe.notes || "");
        setSelectedProduct(recipe.product);

        if (recipe.ingredients && recipe.ingredients.length > 0) {
          let nextKey = 0;
          const loadedRows: IngredientRow[] = recipe.ingredients.map((ing) => {
            const matchedIng = allIngredients.find(
              (i) => i.id === ing.ingredient_id.toString()
            );
            const row: IngredientRow = {
              key: nextKey++,
              ingredient_id: ing.ingredient_id.toString(),
              ingredientName: ing.inventory_ingredient?.name || `#${ing.ingredient_id}`,
              quantity: ing.quantity.toString(),
              unit: ing.unit,
              availableUnits: matchedIng ? getAvailableUnits(matchedIng) : [ing.unit],
              is_optional: ing.is_optional,
              waste_factor: ing.waste_factor.toString(),
              notes: ing.notes || "",
              cost: (ing.inventory_ingredient?.cost || 0) * ing.quantity * ing.waste_factor,
            };
            return row;
          });
          setRows(loadedRows);
          setRowKey(nextKey);
        }
      }
    }
    //eslint-disable-next-line
  }, [recipesData, ingredientsData]);

  const products = productsData?.getProducts || [];
  const ingredients: InventoryIngredient[] =
    ingredientsData?.getInventoryIngredients || [];

  // Product search filtering
  const filteredProducts = productSearch
    ? products.filter((p: any) =>
        p.title.toLowerCase().includes(productSearch.toLowerCase())
      )
    : products;

  // Ingredient search filtering
  const filteredIngredients = ingredientSearch
    ? ingredients.filter((ing) =>
        ing.name.toLowerCase().includes(ingredientSearch.toLowerCase())
      )
    : ingredients;

  // Calculate total food cost (applying unit conversion)
  const totalCost = rows.reduce((sum, row) => {
    if (!row.ingredient_id || !row.quantity) return sum;
    const qty = parseFloat(row.quantity) || 0;
    const waste = parseFloat(row.waste_factor) || 1;
    const ing = ingredients.find((i) => i.id === row.ingredient_id);
    if (!ing) return sum;
    const convFactor = getConversionToBase(row.unit, ing);
    const unitCost = ing.cost || 0;
    return sum + qty * waste * convFactor * unitCost;
  }, 0);

  const productPrice = selectedProduct?.price || 0;
  const margin = productPrice > 0 ? productPrice - totalCost : 0;
  const marginPct = productPrice > 0 ? (margin / productPrice) * 100 : 0;

  const selectProduct = (product: any) => {
    setProductId(product.id);
    setSelectedProduct(product);
    setProductSearch("");
    setShowProductDropdown(false);
  };

  const selectIngredient = (ing: InventoryIngredient, rowIndex: number) => {
    const updated = [...rows];
    updated[rowIndex] = {
      ...updated[rowIndex],
      ingredient_id: ing.id,
      ingredientName: ing.name,
      unit: ing.unit,
      availableUnits: getAvailableUnits(ing),
      cost: (ing.cost || 0) * (parseFloat(updated[rowIndex].quantity) || 0),
    };
    setRows(updated);
    setIngredientSearch("");
    setActiveRowIndex(null);
  };

  const updateRow = (index: number, field: string, value: any) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const addRow = () => {
    setRows([...rows, emptyRow(rowKey)]);
    setRowKey(rowKey + 1);
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!productId) {
      toast.error(lang("Selecciona un producto", "Select a product"));
      return;
    }
    const validRows = rows.filter((r) => r.ingredient_id && r.quantity);
    if (validRows.length === 0) {
      toast.error(
        lang(
          "Agrega al menos un ingrediente",
          "Add at least one ingredient"
        )
      );
      return;
    }

    const ingredientInputs: RecipeIngredientInput[] = validRows.map((r) => ({
      ingredient_id: r.ingredient_id,
      quantity: parseFloat(r.quantity),
      unit: r.unit,
      is_optional: r.is_optional,
      waste_factor: parseFloat(r.waste_factor) || 1,
      notes: r.notes || undefined,
    }));

    try {
      if (isEdit) {
        await updateRecipe({
          variables: {
            id,
            name: name || null,
            yield_qty: parseFloat(yieldQty) || 1,
            notes: notes || null,
            ingredients: ingredientInputs,
          },
        });
        toast.success(lang("Receta actualizada", "Recipe updated"));
      } else {
        await createRecipe({
          variables: {
            product_id: parseInt(productId),
            size_id: sizeId ? parseInt(sizeId) : null,
            name: name || null,
            yield_qty: parseFloat(yieldQty) || 1,
            notes: notes || null,
            ingredients: ingredientInputs,
          },
        });
        toast.success(lang("Receta creada", "Recipe created"));
      }
      history.push("/inventory/recipes");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRecipe({ variables: { id } });
      toast.success(lang("Receta eliminada", "Recipe deleted"));
      history.push("/inventory/recipes");
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <LoaderDots fullSize />;
  }

  return (
    <>
      <p>
        <Link to="/inventory/recipes">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Recetas", "Recipes")}
        </Link>
      </p>
      <h1>
        {isEdit
          ? lang("Editar Receta", "Edit Recipe")
          : lang("Nueva Receta", "New Recipe")}
      </h1>

      <div className="row">
        {/* Left: Product + recipe info */}
        <div className="col-lg-4">
          <div className="material_card p-3 mb-3">
            <h4>{lang("Producto", "Product")}</h4>

            {/* Product selector */}
            {!isEdit && (
              <div className="form-group position-relative">
                <label>{lang("Buscar producto", "Search product")} *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={lang("Escribe para buscar...", "Type to search...")}
                  value={
                    selectedProduct
                      ? selectedProduct.title
                      : productSearch
                  }
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setSelectedProduct(null);
                    setProductId("");
                    setShowProductDropdown(true);
                  }}
                  onFocus={() => setShowProductDropdown(true)}
                />
                {showProductDropdown && !selectedProduct && (
                  <div
                    className="list-group position-absolute w-100"
                    style={{ zIndex: 10, maxHeight: 200, overflowY: "auto" }}
                  >
                    {filteredProducts.slice(0, 20).map((p: any) => (
                      <button
                        key={p.id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => selectProduct(p)}
                      >
                        {p.title}{" "}
                        <small className="text-muted">${p.price}</small>
                      </button>
                    ))}
                    {filteredProducts.length === 0 && (
                      <div className="list-group-item text-muted">
                        {lang("Sin resultados", "No results")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isEdit && selectedProduct && (
              <div className="mb-3">
                <strong>{selectedProduct.title}</strong>
                <span className="text-muted ml-2">
                  ${selectedProduct.price}
                </span>
              </div>
            )}

            {/* Size selector */}
            {selectedProduct?.has_size && selectedProduct?.sizes?.length > 0 && !isEdit && (
              <div className="form-group">
                <label>{lang("Tamaño", "Size")}</label>
                <select
                  className="custom-select"
                  value={sizeId}
                  onChange={(e) => setSizeId(e.target.value)}
                >
                  <option value="">
                    {lang("Sin tamaño (default)", "No size (default)")}
                  </option>
                  {selectedProduct.sizes.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.name} — ${s.price}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>{lang("Nombre de receta", "Recipe name")}</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang(
                  "Ej: Hamburguesa Grande",
                  "E.g. Large Burger"
                )}
              />
              <small className="form-text text-muted">
                {lang("Opcional, para identificar variantes", "Optional, to identify variants")}
              </small>
            </div>

            <div className="form-group">
              <label>{lang("Rendimiento (porciones)", "Yield (servings)")}</label>
              <input
                type="number"
                className="form-control"
                value={yieldQty}
                onChange={(e) => setYieldQty(e.target.value)}
                step="0.01"
                min="0.01"
              />
            </div>

            <div className="form-group">
              <label>{lang("Notas", "Notes")}</label>
              <textarea
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          {/* Cost summary card */}
          <div className="material_card p-3 mb-3">
            <h4>{lang("Resumen de costo", "Cost summary")}</h4>
            <table className="table table-sm mb-0">
              <tbody>
                <tr>
                  <td>{lang("Costo de ingredientes", "Ingredient cost")}</td>
                  <td className="text-right font-weight-bold">
                    ${totalCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td>{lang("Precio de venta", "Sale price")}</td>
                  <td className="text-right">${productPrice.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>{lang("Margen", "Margin")}</td>
                  <td
                    className={`text-right font-weight-bold ${
                      marginPct < 30
                        ? "text-danger"
                        : marginPct < 50
                        ? "text-warning"
                        : "text-success"
                    }`}
                  >
                    ${margin.toFixed(2)} ({marginPct.toFixed(1)}%)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between">
            {isEdit && (
              <button
                className="btn btn-outline-danger"
                onClick={() => setShowDelete(true)}
              >
                <i className="fa fa-trash"></i> {lang("Eliminar", "Delete")}
              </button>
            )}
            <button className="btn btn-primary" onClick={handleSave}>
              <i className="far fa-save"></i> {lang("Guardar", "Save")}
            </button>
          </div>
        </div>

        {/* Right: Ingredient rows */}
        <div className="col-lg-8">
          <div className="material_card p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">
                {lang("Ingredientes de la receta", "Recipe ingredients")}
              </h4>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={addRow}
              >
                <i className="fa fa-plus"></i> {lang("Agregar", "Add")}
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-sm">
                <thead className="thead-light">
                  <tr>
                    <th style={{ minWidth: 200 }}>
                      {lang("Ingrediente", "Ingredient")}
                    </th>
                    <th style={{ width: 100 }}>
                      {lang("Cantidad", "Qty")}
                    </th>
                    <th style={{ width: 120 }}>
                      {lang("Unidad", "Unit")}
                    </th>
                    <th style={{ width: 80 }}>
                      {lang("Merma", "Waste")}
                    </th>
                    <th style={{ width: 60 }}>
                      {lang("Opc.", "Opt.")}
                    </th>
                    <th style={{ width: 80 }}>
                      {lang("Costo", "Cost")}
                    </th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const qty = parseFloat(row.quantity) || 0;
                    const waste = parseFloat(row.waste_factor) || 1;
                    const ing = ingredients.find(
                      (i) => i.id === row.ingredient_id
                    );
                    const convFactor = ing
                      ? getConversionToBase(row.unit, ing)
                      : 1;
                    const rowCost =
                      qty * waste * convFactor * (ing?.cost || 0);

                    return (
                      <tr key={row.key}>
                        <td className="position-relative">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={lang("Buscar...", "Search...")}
                            value={
                              activeRowIndex === index
                                ? ingredientSearch
                                : row.ingredientName
                            }
                            onChange={(e) => {
                              setIngredientSearch(e.target.value);
                              setActiveRowIndex(index);
                            }}
                            onFocus={() => {
                              setActiveRowIndex(index);
                              setIngredientSearch("");
                            }}
                            onBlur={() => {
                              // Delay to allow click on dropdown
                              setTimeout(() => setActiveRowIndex(null), 200);
                            }}
                          />
                          {activeRowIndex === index && (
                            <div
                              className="list-group position-absolute w-100"
                              style={{
                                zIndex: 10,
                                maxHeight: 150,
                                overflowY: "auto",
                              }}
                            >
                              {filteredIngredients
                                .slice(0, 15)
                                .map((ingredient) => (
                                  <button
                                    key={ingredient.id}
                                    type="button"
                                    className="list-group-item list-group-item-action py-1"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() =>
                                      selectIngredient(ingredient, index)
                                    }
                                  >
                                    <small>
                                      {ingredient.name}{" "}
                                      <span className="text-muted">
                                        ({ingredient.unit}
                                        {ingredient.cost
                                          ? ` — $${Number(
                                              ingredient.cost
                                            ).toFixed(2)}`
                                          : ""}
                                        )
                                      </span>
                                    </small>
                                  </button>
                                ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.quantity}
                            onChange={(e) =>
                              updateRow(index, "quantity", e.target.value)
                            }
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td>
                          <select
                            className="custom-select custom-select-sm"
                            value={row.unit}
                            onChange={(e) =>
                              updateRow(index, "unit", e.target.value)
                            }
                            disabled={!row.ingredient_id}
                          >
                            {row.availableUnits.length > 0
                              ? row.availableUnits.map((u) => (
                                  <option key={u} value={u}>
                                    {u}
                                  </option>
                                ))
                              : UNIT_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.value}
                                  </option>
                                ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.waste_factor}
                            onChange={(e) =>
                              updateRow(index, "waste_factor", e.target.value)
                            }
                            step="0.01"
                            min="1"
                            title={lang(
                              "Factor de merma (1 = sin merma)",
                              "Waste factor (1 = no waste)"
                            )}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={row.is_optional}
                            onChange={(e) =>
                              updateRow(index, "is_optional", e.target.checked)
                            }
                          />
                        </td>
                        <td className="text-right">
                          <small className="text-muted">
                            ${rowCost.toFixed(2)}
                          </small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeRow(index)}
                            disabled={rows.length <= 1}
                          >
                            <i className="fa fa-times"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-weight-bold">
                    <td colSpan={5} className="text-right">
                      {lang("Costo total", "Total cost")}
                    </td>
                    <td className="text-right">${totalCost.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      <DeleteModal
        open={showDelete}
        title={lang("¿Eliminar esta receta?", "Delete this recipe?")}
        toggle={() => setShowDelete(false)}
        handleConfirm={handleDelete}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "La receta se eliminará permanentemente",
            "The recipe will be permanently deleted"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default RecipeForm;
